import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-edit-start-date-modal',
  templateUrl: './edit-start-date-modal.component.html',
  styleUrls: ['./edit-start-date-modal.component.scss'],
})
export class EditStartDateModalComponent implements OnChanges {
  /** Controla la visibilidad del modal (one-way desde el padre). */
  @Input() visible = false;
  /** Fecha actual del período 1 (Date a medianoche local del día UTC correspondiente). */
  @Input() currentFirstPeriodDate: Date | null = null;
  /** Total de períodos que se recalcularán en cascada. */
  @Input() periodsCount = 0;
  /** Períodos ya PAGADOS o ATRASADOS que serán recorridos (gatilla la confirmación extra). */
  @Input() paidPeriodsCount = 0;
  /** Estado de guardado (deshabilita el botón). */
  @Input() loading = false;

  @Output() onHide = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<{ startDate: string; reason: string; confirmPaidPeriods: boolean }>();

  form: FormGroup;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      startDate: [null, Validators.required],
      reason: ['', [Validators.required, Validators.maxLength(500)]],
      confirmPaidPeriods: [false],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Al abrir el modal: presetear la fecha al período 1 actual, limpiar el resto
    // y exigir confirmación si hay períodos pagados/atrasados.
    if (changes['visible'] && this.visible) {
      this.form.reset({
        startDate: this.currentFirstPeriodDate ? new Date(this.currentFirstPeriodDate) : null,
        reason: '',
        confirmPaidPeriods: false,
      });

      const confirmCtrl = this.form.get('confirmPaidPeriods');
      if (this.paidPeriodsCount > 0) {
        confirmCtrl?.setValidators(Validators.requiredTrue);
      } else {
        confirmCtrl?.clearValidators();
      }
      confirmCtrl?.updateValueAndValidity();
    }
  }

  /**
   * Cierra cuando el usuario usa la X o la tecla Escape.
   *
   * PrimeNG no cierra el diálogo por su cuenta: `close()` solo emite `visibleChange` y espera que el
   * padre baje la bandera. Con `[visible]` de una sola vía nadie lo escuchaba, así que la X no hacía
   * nada y `onHide` —que se emite al terminar la animación de salida— nunca llegaba. «Cancelar» sí
   * cerraba porque llama a `hide()` directo. Aquí se traduce a esa misma salida.
   */
  onVisibleChange(open: boolean): void {
    if (!open) this.hide();
  }

  hide(): void {
    this.onHide.emit();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { startDate, reason, confirmPaidPeriods } = this.form.value;
    this.onSave.emit({
      startDate: this.toUtcIso(startDate),
      reason: (reason || '').trim(),
      confirmPaidPeriods: !!confirmPaidPeriods,
    });
  }

  // p-calendar entrega un Date en hora local. Lo normalizamos a medianoche UTC del mismo
  // día calendario para evitar el desfase de día (-1) contra el backend (que opera en UTC).
  private toUtcIso(date: Date): string {
    const d = new Date(date);
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
  }
}
